/**
 * IntegrationSelfTest
 *
 * Admin widget that hits /api/admin/integration-selftest and shows a
 * pass/fail list per claim. Lets the operator verify in real time that
 * every integration claim is true RIGHT NOW (not just \"should work\").
 *
 * Each row is color-coded by severity and ok-state, with a fix hint
 * shown when applicable.
 */

import { useAdminAuth } from "@/hooks/useAdminAuth";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, AlertTriangle, Info, RefreshCw, Loader2, MessageSquare } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

interface Check {
  id: string;
  ok: boolean;
  label: string;
  detail: string;
  severity: "critical" | "important" | "info";
  fix?: string;
}
interface Summary {
  total: number;
  ok: number;
  failing_critical: number;
  failing_important: number;
  failing_info: number;
  elapsed_ms: number;
}
interface Response {
  generated_at: string;
  summary: Summary;
  checks: Check[];
}

function StatusIcon({ check }: { check: Check }) {
  if (check.ok) return <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />;
  switch (check.severity) {
    case "critical":
      return <XCircle className="h-4 w-4 text-red-500 shrink-0" />;
    case "important":
      return <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />;
    case "info":
    default:
      return <Info className="h-4 w-4 text-zinc-400 shrink-0" />;
  }
}

function rowClass(check: Check): string {
  if (check.ok) return "border-emerald-500/20 bg-emerald-500/5";
  switch (check.severity) {
    case "critical":  return "border-red-500/30 bg-red-500/5";
    case "important": return "border-amber-500/30 bg-amber-500/5";
    case "info":      return "border-zinc-500/20 bg-zinc-500/5";
  }
}

interface SlackTestResult {
  /** Project-wide policy: ONE canonical Slack env var (SLACK_FUNNEL_WEBHOOK_URL).
   *  Categorization (funnel vs conversion vs error) lives in message formatting. */
  ok: boolean;
  env: { SLACK_FUNNEL_WEBHOOK_URL: boolean };
  attempted_url: string | null;
  slack_response: { status: number; body: string } | null;
  error: string | null;
}

export default function IntegrationSelfTest() {
  const { getAuthHeaders } = useAdminAuth();
  const [data, setData] = useState<Response | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Slack test state
  const [slackTesting, setSlackTesting] = useState(false);
  const [slackResult, setSlackResult] = useState<SlackTestResult | null>(null);
  const getAuthHeadersRef = useRef(getAuthHeaders);
  getAuthHeadersRef.current = getAuthHeaders;

  const runSelfTest = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeadersRef.current();
      const res = await fetch("/api/admin/integration-selftest", { headers });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = (await res.json()) as Response;
      setData(json);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  const runSlackTest = useCallback(async () => {
    setSlackTesting(true);
    setSlackResult(null);
    try {
      const headers = await getAuthHeadersRef.current();
      const res = await fetch("/api/admin/test-slack-notification", { method: "POST", headers });
      const json = (await res.json()) as SlackTestResult;
      setSlackResult(json);
    } catch (e) {
      setSlackResult({
        ok: false,
        env: { SLACK_FUNNEL_WEBHOOK_URL: false },
        attempted_url: null,
        slack_response: null,
        error: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setSlackTesting(false);
    }
  }, []);

  useEffect(() => { void runSelfTest(); }, [runSelfTest]);

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              Self-test integración
              {data && (
                <span className="text-xs font-normal tabular-nums text-zinc-500">
                  {data.summary.ok}/{data.summary.total} ok · {data.summary.elapsed_ms}ms
                </span>
              )}
            </CardTitle>
            <CardDescription className="text-xs">
              Cada claim verificado en runtime contra producción real (DB, KV, env, tabla, eventos recientes).
            </CardDescription>
          </div>
          <Button
            onClick={() => void runSelfTest()}
            disabled={loading}
            variant="outline"
            size="sm"
            type="button"
          >
            <RefreshCw className={`mr-2 h-3 w-3 ${loading ? "animate-spin" : ""}`} />
            Re-run
          </Button>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {error && (
          <div className="rounded-md border border-red-500/30 bg-red-500/5 p-3 text-xs text-red-300">
            {error}
          </div>
        )}

        {!data && loading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-zinc-500" />
          </div>
        )}

        {data && (
          <>
            {/* Top-line summary */}
            <div className="mb-3 grid grid-cols-3 gap-2 text-xs">
              <div className="rounded bg-emerald-500/10 px-2 py-1 text-center">
                <span className="font-semibold text-emerald-400">{data.summary.ok}</span> OK
              </div>
              <div className="rounded bg-red-500/10 px-2 py-1 text-center">
                <span className="font-semibold text-red-400">{data.summary.failing_critical}</span> críticos
              </div>
              <div className="rounded bg-amber-500/10 px-2 py-1 text-center">
                <span className="font-semibold text-amber-400">{data.summary.failing_important}</span> importantes
              </div>
            </div>

            {/* Check list */}
            <div className="space-y-1.5">
              {data.checks.map((c) => (
                <div
                  key={c.id}
                  className={`flex items-start gap-2 rounded-md border px-2.5 py-1.5 text-xs ${rowClass(c)}`}
                >
                  <StatusIcon check={c} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium text-zinc-200">{c.label}</span>
                      <code className="font-mono text-[9px] text-zinc-500">{c.id}</code>
                    </div>
                    <p className="mt-0.5 text-zinc-400">{c.detail}</p>
                    {c.fix && !c.ok && (
                      <p className="mt-1 rounded bg-zinc-900/50 px-2 py-1 text-[10px] text-amber-300">
                        💡 <strong>Fix:</strong> {c.fix}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <p className="mt-3 text-[10px] text-zinc-500">
              Generado: {new Date(data.generated_at).toLocaleString("es-AR")}
            </p>

            {/* Slack live-fire test — only sends a real message to Slack when
                the operator clicks. This is the only way to confirm the
                webhook is reachable without waiting for a real conversion. */}
            <div className="mt-4 rounded-md border border-zinc-800 bg-zinc-900/40 p-3">
              <div className="mb-2 flex items-start justify-between gap-2">
                <div>
                  <p className="text-xs font-semibold text-zinc-200">¿Slack no recibe nada?</p>
                  <p className="mt-0.5 text-[11px] text-zinc-500">
                    Mandá un mensaje de prueba real al webhook configurado. Si #anaafernandez lo recibe, todo el funnel también lo recibirá.
                  </p>
                </div>
                <Button
                  onClick={() => void runSlackTest()}
                  disabled={slackTesting}
                  size="sm"
                  className="shrink-0 bg-amber-500 hover:bg-amber-600 text-black"
                >
                  {slackTesting ? <Loader2 className="mr-2 h-3 w-3 animate-spin" /> : <MessageSquare className="mr-2 h-3 w-3" />}
                  Mandar test
                </Button>
              </div>

              {slackResult && (
                <div
                  className={`mt-2 rounded border p-2 text-[11px] ${
                    slackResult.ok
                      ? "border-emerald-500/30 bg-emerald-500/5"
                      : "border-red-500/30 bg-red-500/5"
                  }`}
                >
                  {slackResult.ok ? (
                    <div className="flex items-start gap-2">
                      <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" />
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-emerald-300">
                          ✓ Slack respondió OK — chequeá #anaafernandez
                        </p>
                        <p className="mt-0.5 text-zinc-500">
                          Webhook: <code className="font-mono">{slackResult.attempted_url}</code>
                        </p>
                        <p className="text-zinc-500">
                          Source: <code className="font-mono">SLACK_FUNNEL_WEBHOOK_URL</code> (único webhook unificado)
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start gap-2">
                      <XCircle className="h-4 w-4 shrink-0 text-red-500" />
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-red-300">✗ Slack falló</p>
                        <p className="mt-0.5 text-zinc-400">{slackResult.error}</p>
                        <div className="mt-1 text-[10px]">
                          <span className={slackResult.env.SLACK_FUNNEL_WEBHOOK_URL ? "text-emerald-400" : "text-zinc-500"}>
                            {slackResult.env.SLACK_FUNNEL_WEBHOOK_URL ? "✓" : "✗"} SLACK_FUNNEL_WEBHOOK_URL <span className="text-zinc-600">(único webhook del proyecto)</span>
                          </span>
                        </div>
                        {slackResult.slack_response && (
                          <p className="mt-1 text-zinc-500">
                            Slack: HTTP {slackResult.slack_response.status} — <code className="font-mono">{slackResult.slack_response.body || "(empty)"}</code>
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
